/*----------------------------------
- LANDING: HOME DOMAINS PREVIEW
----------------------------------*/

/**
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0028
 * @rule Home draws only rows the bare `/domains` search returned, in the engine's order, and asks
 *       Product for `HOME_DOMAINS_PREVIEW_RESULTS_LIMIT` of them (`previewRowLimit`), so no extra
 *       row ever reaches the page data. An unready resolution hides the section; Home never
 *       invents fallback rows.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { HOME_DOMAINS_PREVIEW_RESULTS_LIMIT } from '@uniquedomains/components/contracts/search/searchAccessCatalog';
// Relative on purpose: the root vitest config maps `@/` to the Product app, and this selector
// has a behaviour test there (tests/unit/home-domains-preview-select.test.ts).
import { isRenderablePublicSearchResolution } from '../domains/lib/publicSearchReadiness';

import type { WebsitePublicSearchProjection, WebsitePublicSearchResolution } from '@/common/types/domains/publicSearch';
import type { WebsiteDomainsLandingResponse, WebsiteDomainsSearchRow } from '@/common/types/domains/websiteLanding';

/*----------------------------------
- TYPES
----------------------------------*/

export type HomeDomainsPreviewData = {
    search: WebsiteDomainsLandingResponse;
    rows: WebsiteDomainsSearchRow[];
    projection: WebsitePublicSearchProjection | null;
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

/**
 * What Home draws from the resolution, or null when there is nothing renderable.
 *
 * The cap is applied again here although Product already cut the list: it costs nothing and keeps
 * the rule true during a deploy where the website is live before the Product that honours
 * `previewRowLimit`.
 */
export const selectHomeDomainsPreview = (
    resolution: WebsitePublicSearchResolution | null | undefined,
): HomeDomainsPreviewData | null => {
    if (!isRenderablePublicSearchResolution(resolution)) return null;

    const search = resolution.initialSearch;
    const rows = search?.list?.slice(0, HOME_DOMAINS_PREVIEW_RESULTS_LIMIT) ?? [];
    if (!search || !rows.length) return null;

    return { search, rows, projection: resolution.initialProjection ?? null };
};
